import { useTranslation } from 'react-i18next';
import { type DebriefStage, DebriefFactSource } from '@momentum/types';
import { cn } from '@/shared/utils/utils';
import type { EditableFact } from '../../shared/types';
import { STAGE_META } from '../../gather-facts-stage/utils/stageMeta';
import { IntegrationIcon } from '../../gather-facts-stage/components/IntegrationIcon';
import { BotBubble } from './BotBubble';
import { UserBubble } from './UserBubble';
import { DeepDiveFactRow } from './DeepDiveFactRow';

interface FactPickTurnProps {
  prompt: string;
  facts: EditableFact[];
  stage: DebriefStage;
  pickedFactId: string | null;
  isActive: boolean;
  showTypingDots: boolean;
  onPick: (factId: string) => void;
}

export function FactPickTurn({
  prompt,
  facts,
  stage,
  pickedFactId,
  isActive,
  showTypingDots,
  onPick,
}: FactPickTurnProps) {
  const pickedFact = pickedFactId
    ? facts.find((fact) => fact.id === pickedFactId) ?? null
    : null;

  return (
    <>
      <BotBubble showTypingDots={showTypingDots}>{prompt}</BotBubble>

      {pickedFact ? (
        <UserBubble>
          <PickedFactSummary fact={pickedFact} stage={stage} />
        </UserBubble>
      ) : (
        <div className="self-stretch rounded-xl border border-slate-200 bg-white p-2 deep-dive-slide-fade">
          <ul className="flex flex-col">
            {facts.map((fact) => (
              <li key={fact.id}>
                <DeepDiveFactRow
                  fact={fact}
                  isFaded={!isActive}
                  onPick={() => onPick(fact.id)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

interface PickedFactSummaryProps {
  fact: EditableFact;
  stage: DebriefStage;
}

function PickedFactSummary({ fact, stage }: PickedFactSummaryProps) {
  const { t } = useTranslation('debriefV2');
  const meta = STAGE_META[stage];
  const aiSource =
    fact.source !== DebriefFactSource.Manual ? fact.source : null;

  return (
    <span>
      {fact.description}
      <span
        className={cn(
          'inline-flex items-center gap-1 ms-2 px-2 py-0.5 rounded-md border text-[11px] font-semibold align-text-bottom whitespace-nowrap leading-none cursor-default',
          aiSource === DebriefFactSource.Jira
            ? 'bg-blue-50 border-blue-100 text-blue-700'
            : 'bg-slate-50 border-slate-200 text-slate-600'
        )}
        title={t(meta.labelKey)}
      >
        {aiSource ? (
          <IntegrationIcon source={aiSource} className="h-3 w-3" />
        ) : null}
        {fact.title || t(meta.labelKey)}
      </span>
    </span>
  );
}
